import Grid from 'Geodata/grid';
import { SensorPoint } from 'Simulations/SensorPoint';
import { Ground } from '@data/geodata/legacy/Ground';
import { Roads } from '@data/geodata/legacy/Roads';
import { Targets } from '@data/geodata/legacy/Targets';
import { BuildingSource, PreProcessingTypes, type RawDataGroup, type RawDataObject } from '@visualizer/types';
import type { LatestBuildingsSchema } from './BuildingsSerializer';
import type { LatestGridSchema } from './GridSerializer';
import type { LatestGroundSchema } from './GroundSerializer';
import type { LatestRoadsSchema } from './RoadsSerializer';
import type { LatestTargetsSchema } from './TargetsSerializer';
import type { LatestTreesSchema } from './TreesSerializer';
import FVector from '@data/geometry/FVector';

function setBorder(map: Map<string, number[]>, points: FVector[] | undefined): void {
    const length = points?.length ?? 0;
    const xs = new Float32Array(length) as unknown as number[];
    const ys = new Float32Array(length) as unknown as number[];
    const zs = new Float32Array(length) as unknown as number[];

    for (let i = 0; i < length; i++) {
        const point = points![i];
        xs[i] = point.x;
        ys[i] = point.y;
        zs[i] = point.z;
    }

    map.set('x', xs);
    map.set('y', ys);
    map.set('z', zs);
}

function ensureBoundingBox(points: FVector[] | undefined): FVector[] {
    if (points && points.length >= 4) return points.slice(0, 4);
    const filled = new Array(4).fill(null).map(() => new FVector());
    if (points) {
        for (let i = 0; i < points.length && i < 4; i++) {
            filled[i] = points[i];
        }
    }
    return filled;
}

function computeGridVerticesAndNeighbors(): void {
    (Grid as any).uniqueVertices = [];
    const vertexIndexMap = new Map<string, any>();

    function computeVertices(center: FVector, gridRes: number) {
        const h = gridRes / 2;
        return [
            { x: center.x - h, y: center.y - h, z: center.z - h },
            { x: center.x + h, y: center.y - h, z: center.z - h },
            { x: center.x - h, y: center.y + h, z: center.z - h },
            { x: center.x + h, y: center.y + h, z: center.z - h },
            { x: center.x - h, y: center.y - h, z: center.z + h },
            { x: center.x + h, y: center.y - h, z: center.z + h },
            { x: center.x - h, y: center.y + h, z: center.z + h },
            { x: center.x + h, y: center.y + h, z: center.z + h },
        ];
    }

    function hashVertex(v: { x: number; y: number; z: number }) {
        return `${v.x},${v.y},${v.z}`;
    }

    for (const point of Grid.notNullSensorPoints ?? []) {
        if (!point) continue;
        const rawVertices = computeVertices(point.getCenter(), Grid.gridRes);

        const vertexRefs = rawVertices.map((v) => {
            const key = hashVertex(v);
            if (vertexIndexMap.has(key)) {
                const idx = vertexIndexMap.get(key);
                return (Grid as any).uniqueVertices[idx];
            }

            const vertexObj = { position: v, isActive: false };
            const newIdx = (Grid as any).uniqueVertices.length;
            (Grid as any).uniqueVertices.push(vertexObj);
            vertexIndexMap.set(key, newIdx);
            return vertexObj;
        });

        (point as any).vertices = vertexRefs;
    }

    function normalizeZ(value: number) {
        return Math.round(value / Grid.gridRes);
    }

    function getColumnZKey(x: number, y: number, z: number) {
        return `${x},${y},${normalizeZ(z)}`;
    }

    function getNeighborOffsets26() {
        const offsets = [];
        for (let dx = -1; dx <= 1; dx++) {
            for (let dy = -1; dy <= 1; dy++) {
                for (let dz = -1; dz <= 1; dz++) {
                    if (dx === 0 && dy === 0 && dz === 0) continue;
                    offsets.push({
                        dx,
                        dy,
                        dz,
                        isAxisAlignedStep: Math.abs(dx) + Math.abs(dy) + Math.abs(dz) === 1,
                    });
                }
            }
        }
        return offsets;
    }

    const sensorPointMap = new Map<string, SensorPoint>();
    for (const point of Grid.notNullSensorPoints ?? []) {
        if (!point) continue;
        const [x, y] = Grid.indexToXYZ(point.index);
        sensorPointMap.set(getColumnZKey(x, y, point.getCenter().z), point);
    }

    const offsets26 = getNeighborOffsets26();

    for (const point of Grid.notNullSensorPoints ?? []) {
        if (!point) continue;
        point.neighbors6 = [];
        point.neighbors26 = [];

        // Use X and Y indices (full grid in those dimensions) and center for Z (not affected by grid rotation):
        const [x, y] = Grid.indexToXYZ(point.index);
        const centerZ = point.getCenter().z;
        for (const offset of offsets26) {
            const nx = x + offset.dx;
            const ny = y + offset.dy;
            if (nx < 0 || nx > Grid.gridX || ny < 0 || ny > Grid.gridY) continue;

            const neighbor = sensorPointMap.get(getColumnZKey(nx, ny, centerZ + offset.dz * Grid.gridRes));
            if (neighbor) {
                point.neighbors26.push(neighbor);
                if (offset.isAxisAlignedStep) {
                    point.neighbors6.push(neighbor);
                }
            }
        }
    }
}

export function buildGridFromSchema(schema: LatestGridSchema): Grid {
    const grid = new Grid();

    Grid.METRICcode = schema.EPSG;
    Grid.LAND = schema.LandAnalysis;
    Grid.siteVolumeCenter = schema.siteVolumeCenter;
    Grid.gridAngle = schema.gridAngle;
    Grid.gridOrigin = schema.gridOrigin;
    Grid.siteLat = schema.siteLat;
    Grid.siteLon = schema.siteLon;
    Grid.siteAlt = schema.siteAlt;

    setBorder(Grid.analysisArea, schema.xyz);
    setBorder(Grid.legalBorder, schema.perimeters);
    setBorder(Grid.buildableBorder, schema.buildables ?? []);

    Grid.boundingBox = ensureBoundingBox(schema.boundingBox);

    Grid.gridX = schema.gridX;
    Grid.gridY = schema.gridY;
    Grid.gridZ = schema.gridZ;
    Grid.gridRes = schema.gridRes;

    Grid.sensorPointsN = schema.N1;
    const total = schema.N1 + schema.N2;
    Grid.sensorPoints = new Array(total);
    Grid.allGridPositions = new Array(total);
    Grid.notNullSensorPoints = new Array(schema.N1);

    let counter = 0;
    for (let i = 0; i < schema.Positions.length; i++) {
        const { position, index } = schema.Positions[i];
        Grid.sensorPoints[index] = new SensorPoint(position, counter, index);
        Grid.allGridPositions[index] = position;
        Grid.notNullSensorPoints[i] = new SensorPoint(position, counter, index);
        counter++;
    }

    for (let i = 0; i < schema.NullPositions.length; i++) {
        const { position, index } = schema.NullPositions[i];
        Grid.sensorPoints[index] = new SensorPoint(position);
        Grid.allGridPositions[index] = position;
    }

    Grid.maxFloor = 0;
    for (const sensorPoint of Grid.sensorPoints) {
        if (!sensorPoint || sensorPoint.getID() === -1) continue;
        if (sensorPoint.getFloor() > Grid.maxFloor) {
            Grid.maxFloor = sensorPoint.getFloor();
        }
    }
    if (schema.version <= 9) {
        Grid.maxFloor += 1;
    }

    computeGridVerticesAndNeighbors();
    return grid;
}

export function buildGroundFromSchema(schema: LatestGroundSchema): Ground {
    const ground = new Ground();

    ground.points = schema.points as any;

    const groundMapInternal = new Map<number, unknown>();
    const groundMapMid = new Map<number, unknown>();
    const groundMapExternal = new Map<number, unknown>();

    let cnt = 0;
    for (let i = 0; i < schema.triIDsInt.length; i++) {
        groundMapInternal.set(schema.triIDsInt[i], schema.triangles[cnt]);
        cnt++;
    }
    for (let i = 0; i < schema.triIDsMid.length; i++) {
        groundMapMid.set(schema.triIDsMid[i], schema.triangles[cnt]);
        cnt++;
    }
    for (let i = 0; i < schema.triIDsExt.length; i++) {
        groundMapExternal.set(schema.triIDsExt[i], schema.triangles[cnt]);
        cnt++;
    }

    ground.internTris = groundMapInternal;
    ground.midTris = groundMapMid;
    ground.extTris = groundMapExternal;

    return ground;
}

export function buildRoadsFromSchema(schema: LatestRoadsSchema): Roads {
    const roads = new Roads();
    roads.roads = schema.roads.map((road) => ({ points: road.points as any, emission: road.emission }));

    roads.minEmissionValue = Infinity;
    roads.maxEmissionValue = -Infinity;
    for (const road of roads.roads) {
        if (road.emission < roads.minEmissionValue) roads.minEmissionValue = road.emission;
        if (road.emission > roads.maxEmissionValue) roads.maxEmissionValue = road.emission;
    }

    return roads;
}

export function buildTargetsFromSchema(schema: LatestTargetsSchema): Targets {
    const targets = new Targets();
    targets.targets = schema.targets.map((target) => ({
        kind: target.kind,
        polygons: target.polygons.map((polygon) => ({
            id: polygon.id,
            vertices: polygon.vertices as any,
            indices: polygon.indices,
            middlePoints: polygon.middlePoints as any,
            points: polygon.points as any,
        })),
    }));
    return targets;
}

export function buildTreesFromSchema(schema: LatestTreesSchema): RawDataGroup[] {
    const objects: RawDataObject[] = schema.trees.map((tree) => ({
        points: tree.points as any,
        triangles: tree.triangles as any,
    }));

    return [{ type: PreProcessingTypes.TREES, objects }];
}

export function buildBuildingsFromSchema(schema: LatestBuildingsSchema): RawDataGroup[] {
    const grouped = new Map<PreProcessingTypes, RawDataObject[]>();

    const push = (type: PreProcessingTypes, obj: RawDataObject) => {
        const list = grouped.get(type);
        if (list) list.push(obj);
        else grouped.set(type, [obj]);
    };

    for (const building of schema.buildings) {
        const source = building.source;
        const roof = building.roof;
        const wall = building.wall;
        const floor = building.floor;

        switch (source) {
            case BuildingSource.SKETCH:
                push(PreProcessingTypes.PROJECT_SKETCH_ROOF, roof);
                push(PreProcessingTypes.PROJECT_SKETCH_WALL, wall);
                push(PreProcessingTypes.PROJECT_SKETCH_FLOOR, floor);
                break;
            case BuildingSource.CUSTOM_OBS:
                push(PreProcessingTypes.CUSTOM_BUILDINGS_ROOF, roof);
                push(PreProcessingTypes.CUSTOM_BUILDINGS_WALL, wall);
                push(PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR, floor);
                break;
            case BuildingSource.SWISSTOPO:
            default:
                push(PreProcessingTypes.SWISSTOPO_ROOF, roof);
                push(PreProcessingTypes.SWISSTOPO_WALL, wall);
                push(PreProcessingTypes.SWISSTOPO_FLOOR, floor);
                break;
        }
    }

    const orderedTypes: PreProcessingTypes[] = [
        PreProcessingTypes.PROJECT_SKETCH_ROOF,
        PreProcessingTypes.PROJECT_SKETCH_WALL,
        PreProcessingTypes.PROJECT_SKETCH_FLOOR,
        PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
        PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
        PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
        PreProcessingTypes.SWISSTOPO_ROOF,
        PreProcessingTypes.SWISSTOPO_WALL,
        PreProcessingTypes.SWISSTOPO_FLOOR,
    ];

    const alwaysInclude = new Set<PreProcessingTypes>([
        PreProcessingTypes.SWISSTOPO_ROOF,
        PreProcessingTypes.SWISSTOPO_WALL,
        PreProcessingTypes.SWISSTOPO_FLOOR,
    ]);

    const rawData: RawDataGroup[] = [];
    for (const type of orderedTypes) {
        const objects = grouped.get(type) ?? [];
        if (objects.length > 0 || alwaysInclude.has(type)) {
            rawData.push({ type, objects });
        }
    }

    return rawData;
}

export function buildGeomFromSchema(typeName: string, schema: unknown): unknown {
    switch (typeName) {
        case 'Grid':
            return buildGridFromSchema(schema as LatestGridSchema);
        case 'Ground':
            return buildGroundFromSchema(schema as LatestGroundSchema);
        case 'Roads':
            return buildRoadsFromSchema(schema as LatestRoadsSchema);
        case 'Targets':
            return buildTargetsFromSchema(schema as LatestTargetsSchema);
        case 'Trees':
        case 'Vegetation':
            return buildTreesFromSchema(schema as LatestTreesSchema);
        case 'Buildings':
            return buildBuildingsFromSchema(schema as LatestBuildingsSchema);
        default:
            throw new Error(`Unsupported V2 geometry type: ${typeName}`);
    }
}
